import { useRef, useState, useEffect } from "react";
import { UploadCloud, Trash2, Type, Plus, Pencil, Users } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useFonts } from "@/context/FontsContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

function UserManagement() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/users");
      setUsers(data);
    } catch (e) {
      /* non-admins never render this */
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: "", email: "", password: "", role: "user" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name || "", email: u.email, password: "", role: u.role }); setOpen(true); };

  const roleLabel = (r) => (r === "admin" ? t("settings.roleAdmin") : t("settings.roleUser"));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.patch(`/auth/users/${editing.id}`, payload);
        toast.success(t("settings.userUpdated"));
      } else {
        await api.post("/auth/users", { name: form.name, email: form.email, password: form.password, role: form.role });
        toast.success(t("settings.userCreated"));
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("settings.userSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u) => {
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success(t("settings.userDeleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("settings.userDeleteFailed"));
    }
  };

  const isSelf = (u) => u.email === user?.email;

  return (
    <div className="bhf-card mt-6 p-6" data-testid="user-management-card">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 font-display text-sm font-700 text-slate-800"><Users className="h-4 w-4" /> {t("settings.users")}</h2>
          <p className="mt-1 text-xs text-slate-500">{t("settings.usersDesc")}</p>
        </div>
        <Button size="sm" className="gap-1.5 shrink-0" onClick={openCreate} data-testid="add-user-button">
          <Plus className="h-3.5 w-3.5" /> {t("settings.addUser")}
        </Button>
      </div>

      <div className="mt-5 space-y-2" data-testid="users-list">
        {users.length === 0 && (
          <div className="rounded-md border border-dashed border-slate-200 py-8 text-center text-xs text-slate-400">{t("settings.noUsers")}</div>
        )}
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5" data-testid={`user-row-${u.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium text-slate-800">{u.name || u.email}</span>
                <Badge className={u.role === "admin" ? "bg-indigo-50 text-indigo-600" : "bg-slate-100 text-slate-600"}>{roleLabel(u.role)}</Badge>
                {isSelf(u) && <span className="text-[10px] text-slate-400">· {t("settings.you")}</span>}
              </div>
              <div className="mt-0.5 truncate text-[11px] text-slate-400">{u.email}</div>
            </div>
            <div className="flex items-center gap-1.5">
              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-indigo-600" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              {!isSelf(u) && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-500" data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("settings.deleteUserTitle")}</AlertDialogTitle>
                      <AlertDialogDescription>{t("settings.deleteUserDesc", { email: u.email })}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(u)} data-testid={`confirm-delete-user-${u.id}`}>{t("common.delete")}</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? t("settings.editUserTitle") : t("settings.createUserTitle")}</DialogTitle>
            <DialogDescription>{t("settings.usersDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-slate-500">{t("settings.uName")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">{t("settings.uEmail")}</Label>
              <Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">{editing ? t("settings.uPasswordOpt") : t("settings.uPassword")}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">{t("settings.uRole")}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">{t("settings.roleUser")}</SelectItem>
                  <SelectItem value="admin">{t("settings.roleAdmin")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button className="bhf-btn-primary border-0 text-white" onClick={handleSave} disabled={saving} data-testid="save-user-button">
              {saving ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Settings() {
  const { user, isAdmin } = useAuth();
  const { t } = useTranslation();
  const { fonts, reload } = useFonts();
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const inputRef = useRef(null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["ttf", "otf"].includes(ext)) {
      toast.error(t("settings.onlyTtfOtf"));
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/fonts", form, { headers: { "Content-Type": "multipart/form-data" } });
      await reload();
      toast.success(t("settings.fontImported", { family: data.family }));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || t("settings.importFailed"));
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (font) => {
    setDeleting(font.id);
    try {
      await api.delete(`/fonts/${font.id}`);
      await reload();
      toast.success(t("settings.fontDeleted", { family: font.family }));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || t("settings.deleteFailed"));
    } finally {
      setDeleting(null);
    }
  };

  const roleLabel = user?.role === "admin" ? t("settings.roleAdmin") : t("settings.roleUser");

  return (
    <div className="mx-auto max-w-2xl px-8 py-10" data-testid="settings-page">
      <h1 className="font-display text-2xl font-700 text-slate-900">{t("settings.title")}</h1>

      <div className="bhf-card mt-6 p-6">
        <h2 className="font-display text-sm font-700 text-slate-800">{t("settings.account")}</h2>
        <div className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">{t("settings.name")}</span><span className="text-slate-800">{user?.name}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">{t("settings.email")}</span><span className="text-slate-800">{user?.email}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">{t("settings.role")}</span><span className="text-slate-800">{roleLabel}</span></div>
        </div>
      </div>

      {isAdmin && <UserManagement />}

      <div className="bhf-card mt-6 p-6" data-testid="fonts-card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-sm font-700 text-slate-800">{t("settings.fonts")}</h2>
            <p className="mt-1 text-xs text-slate-500">{t("settings.fontsDesc")}</p>
          </div>
          <input ref={inputRef} type="file" accept=".ttf,.otf,font/ttf,font/otf" className="hidden" onChange={onPick} data-testid="font-file-input" />
          <Button size="sm" className="gap-1.5 shrink-0" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid="import-font-button">
            <UploadCloud className="h-3.5 w-3.5" /> {uploading ? t("settings.importing") : t("settings.importFont")}
          </Button>
        </div>

        <div className="mt-5 space-y-2" data-testid="fonts-list">
          {fonts.length === 0 && (
            <div className="rounded-md border border-dashed border-slate-200 py-8 text-center text-xs text-slate-400" data-testid="fonts-empty">
              <Type className="mx-auto mb-2 h-5 w-5 text-slate-300" />
              {t("settings.noFonts")}
            </div>
          )}
          {fonts.map((f) => (
            <div key={f.id} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5" data-testid={`font-row-${f.id}`}>
              <div className="min-w-0">
                <div className="truncate text-sm text-slate-800" style={{ fontFamily: `'${f.family}'` }} data-testid={`font-preview-${f.id}`}>
                  {f.label || f.family}
                </div>
                <div className="mt-0.5 truncate text-[11px] text-slate-400" style={{ fontFamily: `'${f.family}'` }}>
                  AaBbCc 0123 — {f.family}
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-500" disabled={deleting === f.id} onClick={() => onDelete(f)} data-testid={`delete-font-${f.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="bhf-card mt-6 p-6">
        <h2 className="font-display text-sm font-700 text-slate-800">{t("settings.about")}</h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-500">{t("settings.aboutText")}</p>
      </div>
    </div>
  );
}
